import React, { useState, useEffect, useCallback } from 'react';
import { CostItemGroup, CreateCostItemGroup, UpdateCostItemGroup, Category } from '../types/api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Button,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { apiService } from '../services/api';
import SearchBar from './SearchBar';
import { appColors } from '../theme/colors';
import { listPageDialogPaperProps } from '../theme/darkDialog';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission, permissions } from '../constants/permissions';
import { useDeleteConfirmation } from '../hooks/useDeleteConfirmation';
import {
  ListPageRoot,
  ListPageToolbar,
  ListPageTableSurface,
  ListPageContentSection,
  ListPageMobileStack,
  ListPageTableHeadRow,
  ListPageTh,
  listPageTableBodyClass,
  listPageTableRowClass,
} from './listPage';

interface GroupsTabProps {
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  onRegisterCreateAction?: (handler: (() => void) | null) => void;
}

const GroupsTab: React.FC<GroupsTabProps> = ({ onError, onSuccess, onRegisterCreateAction }) => {
  const { user } = useAuth();
  const { confirmDelete, deleteConfirmationDialog } = useDeleteConfirmation();
  const canCreate = hasPermission(user, permissions.masterData.create);
  const canEdit = hasPermission(user, permissions.masterData.edit);
  const canDelete = hasPermission(user, permissions.masterData.delete);
  const [groups, setGroups] = useState<CostItemGroup[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState<CostItemGroup | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>(undefined);
  const [includeInactive, setIncludeInactive] = useState(false);
  
  const [formData, setFormData] = useState<CreateCostItemGroup>({
    groupCode: '',
    groupName: '',
    description: '',
    categoryId: 0,
    displayOrder: undefined,
    isActive: true,
  });

  const fetchCategories = useCallback(async () => {
    try {
      const result = await apiService.getAllCategories(true);
      setCategories(result);
    } catch (err: any) {
      console.error('Failed to fetch categories:', err);
    }
  }, []);

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiService.getAllGroups(selectedCategoryId, includeInactive);
      // Filter by search term client-side
      let filtered = result;
      if (searchTerm) {
        filtered = result.filter(g => 
          g.groupCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
          g.groupName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (g.description && g.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (g.categoryName && g.categoryName.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }
      setGroups(filtered);
    } catch (err: any) {
      onError('Failed to fetch groups. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [onError, selectedCategoryId, includeInactive, searchTerm]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleOpenDialog = (group?: CostItemGroup) => {
    if (group) {
      setEditingGroup(group);
      setFormData({
        groupCode: group.groupCode,
        groupName: group.groupName,
        description: group.description || '',
        categoryId: group.categoryId,
        displayOrder: group.displayOrder || undefined,
        isActive: group.isActive,
      });
    } else {
      setEditingGroup(null);
      setFormData({
        groupCode: '',
        groupName: '',
        description: '',
        categoryId: selectedCategoryId || 0,
        displayOrder: undefined,
        isActive: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingGroup(null);
    setFormData({
      groupCode: '',
      groupName: '',
      description: '',
      categoryId: selectedCategoryId || 0,
      displayOrder: undefined,
      isActive: true,
    });
  };

  const handleSubmit = async (event: React.MouseEvent) => {
    event.preventDefault();
    if (!formData.categoryId || formData.categoryId === 0) {
      onError('Please select a category');
      return;
    }
    try {
      if (editingGroup) {
        if (!canEdit) {
          onError('You do not have permission to edit groups.');
          return;
        }
        const updateData: UpdateCostItemGroup = {
          groupCode: formData.groupCode,
          groupName: formData.groupName,
          description: formData.description || undefined,
          categoryId: formData.categoryId,
          displayOrder: formData.displayOrder,
          isActive: formData.isActive,
        };
        await apiService.updateGroup(editingGroup.costItemGroupId, updateData);
        onSuccess('Group updated successfully!');
      } else {
        if (!canCreate) {
          onError('You do not have permission to create groups.');
          return;
        }
        await apiService.createGroup(formData);
        onSuccess('Group created successfully!');
      }

      handleCloseDialog();
      fetchGroups();
    } catch (err: any) {
      onError(err.response?.data || 'Operation failed. Please try again.');
    }
  };

  const handleDelete = async (id: number) => {
    if (!canDelete) {
      onError('You do not have permission to delete groups.');
      return;
    }
    const confirmed = await confirmDelete({ message: 'Are you sure you want to delete this group?' });
    if (!confirmed) return;
    try {
      await apiService.deleteGroup(id);
      onSuccess('Group deleted successfully!');
      fetchGroups();
    } catch (err: any) {
      onError(err.response?.data || 'Failed to delete group.');
    }
  };

  useEffect(() => {
    onRegisterCreateAction?.(canCreate ? () => handleOpenDialog() : null);
    return () => onRegisterCreateAction?.(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- register header action only when permission or parent setter changes
  }, [onRegisterCreateAction, canCreate]);

  const getStatusColor = (isActive: boolean) => {
    return isActive
      ? 'text-green-300 bg-green-900/50'
      : 'text-red-300 bg-red-900/50';
  };

  const getStatusDot = (isActive: boolean) => {
    return isActive ? 'bg-green-500' : 'bg-red-500';
  };

  // Color palette for category badges - distinct colors that work on dark background
  const categoryColorPalette = [
    { bg: 'bg-blue-500/20', text: 'text-blue-300', border: 'border-blue-500/30' },
    { bg: 'bg-purple-500/20', text: 'text-purple-300', border: 'border-purple-500/30' },
    { bg: 'bg-pink-500/20', text: 'text-pink-300', border: 'border-pink-500/30' },
    { bg: 'bg-cyan-500/20', text: 'text-cyan-300', border: 'border-cyan-500/30' },
    { bg: 'bg-orange-500/20', text: 'text-orange-300', border: 'border-orange-500/30' },
    { bg: 'bg-yellow-500/20', text: 'text-yellow-300', border: 'border-yellow-500/30' },
    { bg: 'bg-emerald-500/20', text: 'text-emerald-300', border: 'border-emerald-500/30' },
    { bg: 'bg-indigo-500/20', text: 'text-indigo-300', border: 'border-indigo-500/30' },
    { bg: 'bg-rose-500/20', text: 'text-rose-300', border: 'border-rose-500/30' },
    { bg: 'bg-teal-500/20', text: 'text-teal-300', border: 'border-teal-500/30' },
  ];

  const getCategoryColor = (categoryId: number | undefined) => {
    if (!categoryId) {
      return categoryColorPalette[0]; // Default color if no category ID
    }
    return categoryColorPalette[categoryId % categoryColorPalette.length];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <CircularProgress />
      </div>
    );
  }

  return (
    <ListPageRoot>
      <ListPageToolbar className="flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
        <div className="flex flex-col sm:flex-row flex-wrap gap-3 w-full lg:flex-1 lg:min-w-0">
          <SearchBar
            variant="listPage"
            placeholder="Search groups..."
            value={searchTerm}
            onChange={setSearchTerm}
            className="w-full sm:max-w-xs"
          />
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel sx={{ color: appColors.mutedText }}>Filter by Category</InputLabel>
            <Select
              value={selectedCategoryId || ''}
              label="Filter by Category"
              onChange={(e) => setSelectedCategoryId(e.target.value ? Number(e.target.value) : undefined)}
              sx={{
                color: 'white',
                backgroundColor: appColors.borderDark,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: appColors.borderDark,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: appColors.primary,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: appColors.primary,
                },
              }}
            >
              <MenuItem value="">
                <em>All Categories</em>
              </MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.categoryId} value={category.categoryId}>
                  {category.categoryName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: appColors.primary,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: appColors.primary,
                  },
                }}
              />
            }
            label="Include Inactive"
            sx={{ color: 'white' }}
          />
        </div>
      </ListPageToolbar>

      <ListPageContentSection>
        <ListPageTableSurface>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left border-collapse">
              <thead>
                <ListPageTableHeadRow>
                  <ListPageTh>ID</ListPageTh>
                  <ListPageTh>Code</ListPageTh>
                  <ListPageTh>Name</ListPageTh>
                  <ListPageTh>Category</ListPageTh>
                  <ListPageTh className="hidden lg:table-cell">Description</ListPageTh>
                  <ListPageTh className="text-center">Display Order</ListPageTh>
                  <ListPageTh className="text-center">Status</ListPageTh>
                  <ListPageTh className="hidden lg:table-cell">Created</ListPageTh>
                  <ListPageTh className="text-right">Actions</ListPageTh>
                </ListPageTableHeadRow>
              </thead>
              <tbody className={listPageTableBodyClass}>
                {groups.map((group) => (
                  <tr key={group.costItemGroupId} className={listPageTableRowClass}>
                    <td className="px-5 py-4 text-slate-300">#{group.costItemGroupId}</td>
                    <td className="px-5 py-4 text-white font-medium">{group.groupCode}</td>
                    <td className="px-5 py-4 text-white font-medium">{group.groupName}</td>
                    <td className="px-5 py-4">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded border ${getCategoryColor(group.categoryId).bg} ${getCategoryColor(group.categoryId).text} ${getCategoryColor(group.categoryId).border}`}
                      >
                        {group.categoryName || 'N/A'}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-slate-400 hidden lg:table-cell">{group.description || '—'}</td>
                    <td className="px-5 py-4 text-center text-slate-300">{group.displayOrder ?? '—'}</td>
                    <td className="px-5 py-4 text-center">
                      <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(group.isActive)}`}>
                        <span className={`w-2 h-2 mr-2 rounded-full ${getStatusDot(group.isActive)}`} />
                        {group.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-slate-300 hidden lg:table-cell">
                      {group.createdAt ? new Date(group.createdAt).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="action-row-icon-buttons">
                        {canEdit && (
                          <button
                            type="button"
                            className="text-primary hover:bg-primary/10"
                            aria-label="Edit group"
                            onClick={() => handleOpenDialog(group)}
                          >
                            <span className="material-symbols-outlined text-base">edit</span>
                          </button>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            className="text-red-400 hover:bg-red-500/10"
                            aria-label="Delete group"
                            onClick={() => handleDelete(group.costItemGroupId)}
                          >
                            <span className="material-symbols-outlined text-base">delete</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ListPageTableSurface>

        <ListPageMobileStack>
          {groups.map((group) => (
            <div
              key={group.costItemGroupId}
              className="bg-elevation-dark rounded-xl p-4 border border-border-dark shadow-lg flex flex-col gap-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="material-symbols-outlined text-base text-primary shrink-0">folder</span>
                    <h3 className="text-white font-semibold text-lg">{group.groupName}</h3>
                    <span className="text-xs text-slate-500">#{group.costItemGroupId}</span>
                  </div>
                  <p className="text-slate-400 text-sm mb-1">Code: {group.groupCode}</p>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-slate-400 text-sm">Category:</span>
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded border ${getCategoryColor(group.categoryId).bg} ${getCategoryColor(group.categoryId).text} ${getCategoryColor(group.categoryId).border}`}
                    >
                      {group.categoryName || 'N/A'}
                    </span>
                  </div>
                  {group.description && <p className="text-slate-300 text-sm">{group.description}</p>}
                </div>
                <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full shrink-0 ${getStatusColor(group.isActive)}`}>
                  <span className={`w-2 h-2 mr-1 rounded-full ${getStatusDot(group.isActive)}`} />
                  {group.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="flex items-center justify-between text-xs text-slate-400">
                {group.displayOrder != null && (
                  <span className="flex items-center gap-1">
                    <span className="material-symbols-outlined text-sm text-primary">sort</span>
                    Order: {group.displayOrder}
                  </span>
                )}
                {group.createdAt && <span>{new Date(group.createdAt).toLocaleDateString()}</span>}
              </div>

              <div className="action-row-icon-buttons border-t border-border-dark/50 pt-3">
                {canEdit && (
                  <button
                    type="button"
                    className="text-primary hover:bg-primary/10"
                    onClick={() => handleOpenDialog(group)}
                  >
                    <span className="material-symbols-outlined">edit</span>
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    className="text-red-400 hover:bg-red-500/10"
                    onClick={() => handleDelete(group.costItemGroupId)}
                  >
                    <span className="material-symbols-outlined">delete</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </ListPageMobileStack>
      </ListPageContentSection>

      {/* Group Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="sm" 
        fullWidth
        PaperProps={listPageDialogPaperProps}
      >
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600, pb: 1 }}>
          {editingGroup ? `Edit Group - ${editingGroup.groupName}` : 'Create New Group'}
        </DialogTitle>
        
        <DialogContent sx={{ px: 3, pt: '20px' }}>
          <div className="space-y-4">
            <FormControl fullWidth required>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.categoryId}
                label="Category"
                onChange={(e) => setFormData({ ...formData, categoryId: Number(e.target.value) })}
              >
                {categories.map((category) => (
                  <MenuItem key={category.categoryId} value={category.categoryId}>
                    {category.categoryName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              autoFocus
              margin="dense"
              label="Group Code"
              fullWidth
              variant="outlined"
              value={formData.groupCode}
              onChange={(e) => setFormData({ ...formData, groupCode: e.target.value })}
              required
            />
            <TextField
              margin="dense"
              label="Group Name"
              fullWidth
              variant="outlined"
              value={formData.groupName}
              onChange={(e) => setFormData({ ...formData, groupName: e.target.value })}
              required
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Display Order"
              type="number"
              fullWidth
              variant="outlined"
              value={formData.displayOrder || ''}
              onChange={(e) => setFormData({ ...formData, displayOrder: e.target.value ? parseInt(e.target.value) : undefined })}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: appColors.primary,
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: appColors.primary,
                    },
                  }}
                />
              }
              label="Active"
              sx={{ color: 'white' }}
            />
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{ 
              color: appColors.mutedText, 
              borderColor: appColors.dialogBorder,
              '&:hover': {
                borderColor: appColors.primary,
                color: appColors.primary,
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{
              backgroundColor: appColors.primary,
              '&:hover': {
                backgroundColor: appColors.primary,
                opacity: 0.9,
              }
            }}
          >
            {editingGroup ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      {deleteConfirmationDialog}
    </ListPageRoot>
  );
};

export default GroupsTab;

